import { Typography } from '@mui/material';
import CheckRounded from '@mui/icons-material/CheckRounded';
import FlagRounded from '@mui/icons-material/FlagRounded';
import NotificationsActiveRounded from '@mui/icons-material/NotificationsActiveRounded';
import PictureAsPdfRounded from '@mui/icons-material/PictureAsPdfRounded';
import TSButton from 'components/common/Button/Button';
import Notice from 'components/common/Notice/Notice';
import TSCard from 'components/common/TSCard/TSCard';
import MLToast from 'components/common/Toast/Toast';
import usePushNotifications from 'hooks/usePushNotifications';
import './LogSuccessMessage.module.scss';

// A plain <a href download> is ignored by browsers for cross-origin URLs
// (S3 is a different origin) — fetching the file and triggering a
// synthetic click on an object URL is what actually forces a download
// instead of just opening the PDF in a new tab.
const downloadSafetySheet = async (url, showToast) => {
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Request failed: ${response.status}`);
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = 'safety-sheet.pdf';
    link.click();
    URL.revokeObjectURL(objectUrl);
  } catch (error) {
    showToast('Could not download the safety sheet right now.', 'error');
  }
};

const LogSuccessMsg = ({ handleDismiss, safetySheetUrl }) => {
  const { permission, subscribe } = usePushNotifications();
  const { showToast } = MLToast();

  const hasNotificationAction =
    permission === 'default' || permission === 'denied';
  const hasAnythingToDo = hasNotificationAction || !!safetySheetUrl;

  return (
    <div className="ts-checked-in">
      <div className="ts-checked-in__head">
        <span className="ts-checked-in__mark" aria-hidden="true">
          <CheckRounded />
        </span>
        <Typography variant="h2">You are checked in</Typography>
        <Typography variant="body1" color="text.secondary">
          Have a safe walk. We will check on you if you are not back on time.
        </Typography>
      </div>

      {hasAnythingToDo && (
        <section className="flex flex-col gap-3">
          <Typography variant="h3">Before you go</Typography>

          {permission === 'default' && (
            <TSCard
              icon={<NotificationsActiveRounded />}
              title="Get a reminder to check out"
            >
              <Typography variant="body1">
                Allow notifications and we will remind you if you forget to
                check out. We only send safety reminders.
              </Typography>
              <div>
                <TSButton
                  btnText="Allow notifications"
                  btnVariant="outlined"
                  onClickFunc={subscribe}
                />
              </div>
            </TSCard>
          )}

          {permission === 'denied' && (
            <Notice
              tone="warning"
              title="Reminders are blocked in this browser"
            >
              Your browser may have blocked them. Turn on notifications for this
              site in your browser settings to get reminders here instead of by
              SMS.
            </Notice>
          )}

          {safetySheetUrl && (
            <TSCard icon={<PictureAsPdfRounded />} title="Safety sheet">
              <Typography variant="body1">
                Key safety information for this trail, to keep on your phone.
              </Typography>
              <div>
                <TSButton
                  btnText="Download safety sheet"
                  btnVariant="outlined"
                  onClickFunc={() =>
                    downloadSafetySheet(safetySheetUrl, showToast)
                  }
                />
              </div>
            </TSCard>
          )}
        </section>
      )}

      <TSButton
        btnText="Start walking"
        btnSize="large"
        icon={<FlagRounded />}
        onClickFunc={handleDismiss}
      />
    </div>
  );
};

export default LogSuccessMsg;
